import { Component, Input, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { IAssignmentStatus } from 'src/app/interfaces/iassignment-status';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { AppConstants } from 'src/app/app-constants';
import { localeLowerCase } from '../../../../utils/locale-case.util';
@Component({
  selector: 'app-learner-dashboard-tab-content',
  templateUrl: './learner-dashboard-tab-content.component.html',
  styleUrls: ['./learner-dashboard-tab-content.component.scss'],
  standalone: false,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'dashboard',
    },
  ],
})
export class LearnerDashboardTabContentComponent {
  @Input() statusModuleAssignments: IAssignmentStatus[] = [];
  @Input() noAssignmentTitle: string = '';
  @Input() noAssignmentDetails: string = '';

  protected displayAsRows = false;
  protected isResponsiveLayoutEnabled = false;
  public AppConstants = AppConstants;

  constructor(
    private router: Router,
    private amplitudeService: AmplitudeService,
    private splitIoService: SplitIoService,
    private translationService: TranslationService,
    private translocoService: TranslocoService
  ) {}

  async ngOnInit() {
    this.sortModuleAssignments();
    this.displayAsRows = this.statusModuleAssignments.length <= 3;
    this.isResponsiveLayoutEnabled =
      true ||
      (await this.splitIoService.getTreatment(
        'Responsive_Assignments_Dashboard'
      )) === 'on';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['statusModuleAssignments'] && this.statusModuleAssignments?.length) {
      this.sortModuleAssignments();
      this.displayAsRows = this.statusModuleAssignments.length <= 3;
    }
  }

  protected trackById(_index: number, assignment: IAssignmentStatus) {
    return assignment.moduleAssignmentDTO.uuid;
  }

  private isClosed(assignment: IAssignmentStatus): boolean {
    // SAFE: internal status field vs fixed AppConstants
    const topLevelStatus = (assignment.moduleAssignmentProgressStatus || '').toLowerCase();
    // SAFE: internal status field vs fixed AppConstants
    const dtoStatus = (assignment.moduleAssignmentDTO.status || '').toLowerCase();
    return (
      topLevelStatus === AppConstants.CLOSED_ASSIGNMENT_STATUS ||
      dtoStatus === AppConstants.CLOSED_ASSIGNMENT_STATUS
    );
  }

  private isAuthorTesting(assignment: IAssignmentStatus): boolean {
    return assignment.moduleAssignmentDTO.status === 'AUTHOR_TESTING';
  }

  private isActive(assignment: IAssignmentStatus): boolean {
    // SAFE: internal status field vs fixed AppConstants
    const dtoStatus = (assignment.moduleAssignmentDTO.status || '').toLowerCase();
    // Treat null/undefined/empty status as ACTIVE (default behavior)
    // This handles cases where status is null but assignment is in progress
    return !dtoStatus || dtoStatus === AppConstants.ACTIVE_ASSIGNMENT_STATUS;
  }

  private isStaged(assignment: IAssignmentStatus): boolean {
    // SAFE: internal status field vs fixed AppConstants
    const dtoStatus = (assignment.moduleAssignmentDTO.status || '').toLowerCase();
    return dtoStatus === AppConstants.STAGED_ASSIGNMENT_STATUS;
  }

  private isComplete(assignment: IAssignmentStatus): boolean {
    return assignment.moduleAssignmentProgressStatus === 'COMPLETED';
  }

  private hasDueDate(assignment: IAssignmentStatus): boolean {
    return !!assignment.moduleAssignmentDTO.endDate;
  }

  private isPastDue(assignment: IAssignmentStatus): boolean {
    if (!this.hasDueDate(assignment)) {
      return false;
    }
    const currentDate = new Date();
    const dueDate = new Date(assignment.moduleAssignmentDTO.endDate);
    return dueDate < currentDate;
  }

  private isUpcoming(assignment: IAssignmentStatus): boolean {
    const currentDate = new Date();
    const startDate = new Date(assignment.moduleAssignmentDTO.startDate);
    return startDate > currentDate;
  }

  /**
   * Sort function with alphabetical tie-breaker
   * @param a First assignment
   * @param b Second assignment
   * @param dateGetter Function to get the date to compare
   * @param ascending If true, sort ascending (earliest first), if false, sort descending (most recent first)
   */
  private sortByDateWithTieBreaker(
    a: IAssignmentStatus,
    b: IAssignmentStatus,
    dateGetter: (assignment: IAssignmentStatus) => Date,
    ascending: boolean = true
  ): number {
    const dateA = dateGetter(a).getTime();
    const dateB = dateGetter(b).getTime();

    // Handle invalid dates (NaN) - always sort to the end
    const isInvalidA = isNaN(dateA);
    const isInvalidB = isNaN(dateB);
    
    if (isInvalidA && isInvalidB) {
      // Both invalid - use alphabetical tie-breaker
      const locale = this.translocoService.getActiveLang() || 'en';
      // REMEDIATE: assignment/pathway display name sort
      const nameA = localeLowerCase(a.moduleAssignmentDTO.name || '', locale);
      // REMEDIATE: assignment/pathway display name sort
      const nameB = localeLowerCase(b.moduleAssignmentDTO.name || '', locale);
      return nameA.localeCompare(nameB);
    }
    
    if (isInvalidA) return 1; // Invalid dates go to end
    if (isInvalidB) return -1; // Invalid dates go to end

    if (dateA !== dateB) {
      return ascending ? dateA - dateB : dateB - dateA;
    }

    // Tie-breaker: alphabetize by assignment name
    const nameA = this.getSortableAssignmentName(a);
    const nameB = this.getSortableAssignmentName(b);
    return nameA.localeCompare(nameB);
  }

  private getSortableAssignmentName(assignment: IAssignmentStatus): string {
    const isPathway =
      assignment.moduleAssignmentDTO.moduleAssignmentType ===
      AppConstants.TYPE_PATHWAY;

    const name = isPathway
      ? assignment.pathwayInfo?.pathwayName || assignment.moduleAssignmentDTO.name
      : assignment.moduleAssignmentDTO.name;

    // REMEDIATE: sortable assignment/pathway display name
    return localeLowerCase(name ?? '', this.translocoService.getActiveLang() || 'en');
  }

  daysUntilDuePill(statusModuleAssignment: IAssignmentStatus): {
    pillText: string | null;
    pillColor: string | null;
  } {
    const { startDate, endDate } =
      statusModuleAssignment.moduleAssignmentDTO;

    if ((!endDate || !startDate)) {
      return { pillText: null, pillColor: null };
    }

    const currentDate = new Date();
    const startDateObj = new Date(startDate);
    const dueDateObj = new Date(endDate);

    const daysUntilStart = this.calculateDateDifference(
      currentDate,
      startDateObj
    );
    const daysUntilDue = this.calculateDateDifference(currentDate, dueDateObj);

    if (daysUntilStart > 0) {
      return {
        pillText: this.getAvailableInLabel(daysUntilStart),
        pillColor: 'normal',
      };
    }

    if (daysUntilDue < 0) {
      return {
        pillText: this.getPastDueLabel(Math.abs(daysUntilDue)),
        pillColor: 'warning',
      };
    }

    if (daysUntilDue === 0) {
      return {
        pillText: this.getDueTodayLabel(),
        pillColor: 'alert',
      };
    }

    if (daysUntilDue <= 7) {
      return {
        pillText: this.getUntilDueLabel(daysUntilDue),
        pillColor: 'alert',
      };
    }

    if (daysUntilDue <= 30) {
      return {
        pillText: this.getUntilDueLabel(daysUntilDue),
        pillColor: 'normal',
      };
    }
    return { pillText: null, pillColor: null };
  }

  statusPilll(statusModuleAssignment: IAssignmentStatus): {
    pillText: string | null;
    pillColor: string | null;
  } {
    const status = this.isClosed(statusModuleAssignment)
      // SAFE: fixed internal constant
      ? AppConstants.CLOSED_ASSIGNMENT_STATUS.toUpperCase()
      : statusModuleAssignment.moduleAssignmentProgressStatus;

    // SAFE: fixed internal constant
    if (status === AppConstants.CLOSED_ASSIGNMENT_STATUS.toUpperCase()) {
      return {
        pillText: `${this.translationService.translate('dashboard.CLOSED')}`,
        pillColor: 'closed',
      };
    }

    if (status === 'COMPLETED') {
      return {
        pillText: `${this.translationService.translate(
        'COMPLETED',
        'dashboard'
      )}`,
        pillColor: 'success',
      };
    }

    return {
      pillText: `${this.translationService.translate(
        'IN_COMPLETE',
        'dashboard'
      )}`,
      pillColor: 'incomplete',
    };
  }

  calculateDateDifference(date1: Date, date2: Date): number {
    const diffTime = date2.getTime() - date1.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  private getAvailableInLabel(days: number): string {
    return `${this.translationService.translate('AVAILABLE_IN', 'dashboard')} ${days} ${this.translationService.translate(days === 1 ? 'DAY' : 'DAYS', 'dashboard')}`;
  }

  private getPastDueLabel(days: number): string {
    return `${days} ${this.translationService.translate(days === 1 ? 'DAY' : 'DAYS', 'dashboard')} ${this.translationService.translate('PAST_DUE', 'dashboard')}`;
  }

  private getUntilDueLabel(days: number): string {
    return `${days} ${this.translationService.translate(days === 1 ? 'DAY' : 'DAYS', 'dashboard')} ${this.translationService.translate('UNTILL_DUE', 'dashboard')}`;
  }

  private getDueTodayLabel(): string {
    return this.translationService.translate('DUE_TODAY', 'dashboard');
  }

  isAssignmentAvailable(assignment: IAssignmentStatus): boolean {
    if (this.isClosed(assignment)) return true;
    const currentDate = new Date();
    const startDate = new Date(assignment.moduleAssignmentDTO.startDate);

    return currentDate > startDate;
  }

  goToAssignment(assignment: IAssignmentStatus): void {
    const moduleAssignmentUuid = assignment.moduleAssignmentDTO.uuid;
    if (!moduleAssignmentUuid) {
      console.error(
        'No moduleAssignmentUuid found in assignment: ',
        assignment
      );
      return;
    }
    const practiceType = assignment.moduleAssignmentDTO.moduleAssignmentType;
    const returnUrl = `/assignment?moduleAssignmentUuid=${moduleAssignmentUuid}&practiceType=${practiceType}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
    this.router.navigate(['/assignment'], {
      queryParams: {
        moduleAssignmentUuid,
        practiceType,
      },
    });
  }

  sortModuleAssignments(): void {
    // Initialize groups
    const authorTesting: IAssignmentStatus[] = [];
    const incompleteActiveWithDueDate: IAssignmentStatus[] = [];
    const incompleteActiveWithoutDueDate: IAssignmentStatus[] = [];
    const upcomingStaged: IAssignmentStatus[] = [];
    const complete: IAssignmentStatus[] = [];
    const closed: IAssignmentStatus[] = [];
    // Catch-all for any assignments that don't match the above categories
    const incompletePastDue: IAssignmentStatus[] = [];

    // Categorize each assignment into exactly one group
    this.statusModuleAssignments.forEach((assignment) => {
      // Group 7: CLOSED status (checked first as it takes precedence)
      if (this.isClosed(assignment)) {
        closed.push(assignment);
        return;
      }

      // Group 1: AUTHOR_TESTING status (all assignments with this status, regardless of completion)
      if (this.isAuthorTesting(assignment)) {
        authorTesting.push(assignment);
        return;
      }

      // Group 3 & 4: Incomplete ACTIVE status
      if (!this.isComplete(assignment) && this.isActive(assignment)) {
        if (this.hasDueDate(assignment)) {
          // Check if past due - past due assignments go to incompletePastDue
          if (this.isPastDue(assignment)) {
            incompletePastDue.push(assignment);
          } else {
            incompleteActiveWithDueDate.push(assignment);
          }
        } else {
          incompleteActiveWithoutDueDate.push(assignment);
        }
        return;
      }

      // Group 5: Upcoming STAGED status
      if (this.isStaged(assignment) && this.isUpcoming(assignment)) {
        upcomingStaged.push(assignment);
        return;
      }

      // Group 6: All completed assignments (regardless of status)
      if (this.isComplete(assignment)) {
        complete.push(assignment);
        return;
      }

      // Catch-all: Other incomplete assignments that don't match above
      incompletePastDue.push(assignment);
    });

    // Sort Group 1: AUTHOR_TESTING (all assignments with this status) - most recent start date first
    authorTesting.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.startDate),
        false
      )
    );

    // Sort Group 3: Incomplete ACTIVE with Due Dates - past due at top, then earliest due date first
    incompleteActiveWithDueDate.sort((a, b) => {
      const dueDateA = new Date(a.moduleAssignmentDTO.endDate).getTime();
      const dueDateB = new Date(b.moduleAssignmentDTO.endDate).getTime();
      const pastDueA = this.isPastDue(a);
      const pastDueB = this.isPastDue(b);

      // Past due assignments come first
      if (pastDueA && !pastDueB) return -1;
      if (!pastDueA && pastDueB) return 1;

      // If both are past due or both are upcoming, sort by due date (earliest first)
      if (dueDateA !== dueDateB) {
        return dueDateA - dueDateB;
      }

      // Tie-breaker: alphabetize by assignment name
      const nameA = this.getSortableAssignmentName(a);
      const nameB = this.getSortableAssignmentName(b);
      return nameA.localeCompare(nameB);
    });

    // Sort Group 4: Incomplete ACTIVE without Due Dates - most recent start date first
    incompleteActiveWithoutDueDate.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.startDate),
        false
      )
    );

    // Sort Group 5: Upcoming STAGED - earliest start date first
    upcomingStaged.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.startDate),
        true
      )
    );

    // Sort Group 6: Complete - most recent start date first
    complete.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.startDate),
        false
      )
    );

    // Sort Group 7: CLOSED - most recent start date first
    closed.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.startDate),
        false
      )
    );

    // Sort incompletePastDue: earliest due date first (most overdue first)
    incompletePastDue.sort((a, b) =>
      this.sortByDateWithTieBreaker(
        a,
        b,
        (assignment) => new Date(assignment.moduleAssignmentDTO.endDate),
        true
      )
    );

    console.log('AuthorTesting', authorTesting);
    console.log('IncompletePastDue', incompletePastDue);
    console.log('IncompleteActiveWithDueDate', incompleteActiveWithDueDate);
    console.log('IncompleteActiveWithoutDueDate', incompleteActiveWithoutDueDate);
    console.log('UpcomingStaged', upcomingStaged);
    console.log('Complete', complete);
    console.log('Closed', closed);
    console.log('StatusModuleAssignments', this.statusModuleAssignments);


    // Combine all groups in the specified order
    this.statusModuleAssignments = [
      ...authorTesting,
      ...incompletePastDue,
      ...incompleteActiveWithDueDate,
      ...incompleteActiveWithoutDueDate,
      ...upcomingStaged,
      ...complete,
      ...closed,
    ];
  }

  public sendAmplitudeClickViewAssignmentEvent(
    assignment: IAssignmentStatus
  ): void {
    // Sending the Event of Clicking on 'View Assignment' to Amplitude
    this.amplitudeService.trackEvent('assignmentsdashboard_click_assignment', {
      assignment_status: assignment?.moduleAssignmentProgressStatus,
      assignment_type: assignment?.moduleAssignmentDTO?.moduleAssignmentType,
      assignment_duedate: assignment?.moduleAssignmentDTO?.endDate,
    });
  }

  getModuleAssignmentIcon(moduleAssignmentType: string): string {
    if (moduleAssignmentType === AppConstants.TYPE_PRACTICE) {
      return 'assets/icons/practice-icon.svg';
    } else if (moduleAssignmentType === AppConstants.TYPE_CERTIFICATION) {
      return 'assets/icons/certification-icon.svg';
    } else return 'assets/icons/practice-icon.svg';
  }
}
